import React, { useState, useEffect, useCallback } from "react";
import useApp from "../../store/contexts/AppContext";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import { useTheme, alpha } from "@mui/material/styles";
// Icons
import StarIcon from "@mui/icons-material/Star";
import CancelIcon from "@mui/icons-material/Cancel";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import TimerOffIcon from "@mui/icons-material/TimerOff";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { formatDateDurationForJoinedDateExtended } from "../../utils/otherDateFormatter";
import capitalizeEachFirstWord from "../../utils/CapLetters";

export default function AccountActivity({
  userIsCurrentLoggedInUser,
  classes,
}) {
  const theme = useTheme();
  const { getUserActivitiesApiCall, loadANewPage } = useApp();
  const [currentUserActivities, setCurrentUserActivities] = useState({
    current_page: 0,
    page_size: 0,
    next_page: 0,
    total_pages: 0,
    total_records: 0,
    response: [],
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isInitialFetch, setIsInitialFetch] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});

  // Function to fetch user activities with pagination
  const getUserActivities = useCallback(
    async (pageNo = 1, pageSize = 10) => {
      // Don't make API call if we know there's no more data to fetch or if already loading
      if ((!hasMoreData && !isInitialFetch) || (loading && !isInitialFetch))
        return;
      try {
        setLoading(true);
        const userActivities = await getUserActivitiesApiCall({
          pageNo,
          pageSize,
        });
        if (userActivities.success) {
          // Check if the response is empty or if we've reached the total records
          const newData = userActivities.data.response || [];
          const noMoreData =
            newData.length === 0 ||
            !userActivities.data.next_page ||
            userActivities.data.current_page >= userActivities.data.total_pages;
          // Update state
          setCurrentUserActivities((prev) => ({
            ...userActivities.data,
            response:
              pageNo === 1 ? [...newData] : [...prev.response, ...newData],
          }));
          // Mark if we've reached the end of the data
          setHasMoreData(!noMoreData);
          setError(null);
        } else {
          setError(userActivities.message || "Failed to load activities");
          setHasMoreData(false);
        }
      } catch (err) {
        setError("An error occurred while loading activities");
        console.error("Error loading activities:", err);
        setHasMoreData(false);
      } finally {
        setLoading(false);
        setInitialLoading(false);
        setIsInitialFetch(false);
      }
    },
    [getUserActivitiesApiCall, hasMoreData, isInitialFetch, loading]
  );

  // Handle load more button click
  const handleLoadMore = () => {
    if (currentUserActivities.next_page && hasMoreData && !loading) {
      getUserActivities(
        currentUserActivities.next_page,
        currentUserActivities.page_size
      );
    }
  };

  // Initial data fetch when the component mounts
  useEffect(() => {
    if (userIsCurrentLoggedInUser && isInitialFetch) {
      setInitialLoading(true);
      getUserActivities(1, 10);
    }
  }, [userIsCurrentLoggedInUser, getUserActivities, isInitialFetch]);

  // Handle accordion expand/collapse
  const handleAccordionChange = (activityId) => (event, isExpanded) => {
    setExpandedItems((prev) => ({
      ...prev,
      [activityId]: isExpanded,
    }));
  };

  // Function to parse meta_data if it's a string
  const parseMetaData = (metaData) => {
    if (typeof metaData === "string") {
      try {
        return JSON.parse(metaData);
      } catch (e) {
        return metaData;
      }
    }
    return metaData;
  };

  // Helper function to get appropriate icons for different activity types
  const getActivityIcon = (activity) => {
    if (!activity.tag) return null;
    switch (activity.tag) {
      case "premium_upgrade":
      case "premium_reactivated":
      case "standard_upgrade":
      case "standard_reactivated":
      case "elite_upgrade":
      case "elite_reactivated":
        return (
          <StarIcon
            sx={{
              color: "#d44402",
              fontSize: "1.1rem",
            }}
          />
        );
      case "premium_cancel":
      case "standard_cancel":
      case "elite_cancel":
        return (
          <CancelIcon
            sx={{
              color: "#f44336",
              fontSize: "1.1rem",
            }}
          />
        );
      case "premium_renewal":
      case "premium_auto_renew_on":
      case "standard_renewal":
      case "standard_auto_renew_on":
      case "elite_renewal":
      case "elite_auto_renew_on":
        return (
          <AutorenewIcon
            sx={{
              color: "#4caf50",
              fontSize: "1.1rem",
            }}
          />
        );
      case "premium_expired":
      case "premium_auto_renew_off":
      case "standard_expired":
      case "standard_auto_renew_off":
      case "elite_expired":
      case "elite_auto_renew_off":
        return (
          <TimerOffIcon
            sx={{
              color: "#9e9e9e",
              fontSize: "1.1rem",
            }}
          />
        );
      default:
        // Check if tag contains membership-related keywords
        if (
          activity.tag.includes("membership") ||
          activity.tag.includes("standard") ||
          activity.tag.includes("premium") ||
          activity.tag.includes("elite") ||
          activity.description.toLowerCase().includes("membership") ||
          activity.description.toLowerCase().includes("standard") ||
          activity.description.toLowerCase().includes("premium") ||
          activity.description.toLowerCase().includes("elite")
        ) {
          return (
            <StarIcon
              sx={{
                color: "#d44402",
                fontSize: "1.1rem",
              }}
            />
          );
        }
        return null;
    }
  };

  // Render loading state
  if (initialLoading) {
    return (
      <Paper
        elevation={1}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: theme.palette.mode === "dark" ? "#1a1a1f" : "#ffffff",
          border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(212,68,1,0.1)"}`,
          boxShadow: theme.palette.mode === "dark"
            ? "0 4px 20px rgba(0, 0, 0, 0.3)"
            : "0 4px 20px rgba(212, 68, 1, 0.08)",
          transition: "all 0.3s ease",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            padding: "16px 20px",
            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
            backgroundColor: theme.palette.mode === "dark" ? "#222" : "#f8f9fa",
            color: theme.palette.mode === "dark" ? "#fff" : "#111827",
            fontWeight: 600,
            fontSize: "1.1rem",
            margin: 0,
            transition: "all 0.3s ease",
          }}
        >
          My Activities
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "40px 0",
            backgroundColor: theme.palette.mode === "dark" ? "#1a1a1f" : "#ffffff",
            transition: "background-color 0.3s ease",
          }}
        >
          <CircularProgress size={36} sx={{ color: theme.palette.primary.main }} />
        </Box>
      </Paper>
    );
  }

  // Render error state
  if (error) {
    return (
      <Paper
        elevation={1}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: theme.palette.mode === "dark" ? "#1a1a1f" : "#ffffff",
          border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(212,68,1,0.1)"}`,
          boxShadow: theme.palette.mode === "dark"
            ? "0 4px 20px rgba(0, 0, 0, 0.3)"
            : "0 4px 20px rgba(212, 68, 1, 0.08)",
          transition: "all 0.3s ease",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            padding: "16px 20px",
            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
            backgroundColor: theme.palette.mode === "dark" ? "#222" : "#f8f9fa",
            color: theme.palette.mode === "dark" ? "#fff" : "#111827",
            fontWeight: 600,
            fontSize: "1.1rem",
            margin: 0,
            transition: "all 0.3s ease",
          }}
        >
          My Activities
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
            color: theme.palette.error.main,
            textAlign: "center",
            backgroundColor: theme.palette.mode === "dark" ? "#1a1a1f" : "#ffffff",
            transition: "background-color 0.3s ease",
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 40, mb: 2 }} />
          <Typography>{error}</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={1}
      sx={{
        borderRadius: "16px",
        overflow: "hidden",
        backgroundColor: theme.palette.mode === "dark" ? "#1a1a1f" : "#ffffff",
        border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(212,68,1,0.1)"}`,
        boxShadow: theme.palette.mode === "dark"
          ? "0 4px 20px rgba(0, 0, 0, 0.3)"
          : "0 4px 20px rgba(212, 68, 1, 0.08)",
        transition: "all 0.3s ease",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          padding: "16px 20px",
          borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
          backgroundColor: theme.palette.mode === "dark" ? "#222" : "#f8f9fa",
          color: theme.palette.mode === "dark" ? "#fff" : "#111827",
          fontWeight: 600,
          fontSize: "1.1rem",
          margin: 0,
          transition: "all 0.3s ease",
        }}
      >
        My Activities
      </Typography>
      {currentUserActivities.response &&
      currentUserActivities.response.length > 0 ? (
        <>
          <Box
            sx={{
              maxHeight: "400px",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: theme.palette.mode === "dark" ? "#2a2a2a" : "#f0f0f0",
              },
              "&::-webkit-scrollbar-thumb": {
                background: theme.palette.mode === "dark" ? "#555" : "#bbb",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: theme.palette.mode === "dark" ? "#666" : "#999",
              },
            }}
          >
            {currentUserActivities.response.map((userActivity, index) => {
              // Parse meta_data if it's a string
              const metaData = parseMetaData(userActivity.meta_data);
              // Check if this activity should have an icon
              const activityIcon = getActivityIcon(userActivity);
              const activityId = userActivity.id || `activity-${index}`;
              const isExpanded = !!expandedItems[activityId];
              // Check if the description contains premium/membership keywords
              const isPremiumRelated =
                userActivity.description.toLowerCase().includes("membership") ||
                userActivity.description.toLowerCase().includes("standard") ||
                userActivity.description.toLowerCase().includes("premium") ||
                userActivity.description.toLowerCase().includes("elite") ||
                (userActivity.tag &&
                  (userActivity.tag.includes("membership") ||
                    userActivity.tag.includes("standard") ||
                    userActivity.tag.includes("premium") ||
                    userActivity.tag.includes("elite")));

              // Determine background color based on odd/even index
              const isOdd = index % 2 === 0;
              const rowBackgroundColor = theme.palette.mode === "dark"
                ? (isOdd ? "#1a1a1f" : "#232328")
                : (isOdd ? "#f9f9f9" : "#ffffff");

              return (
                <Accordion
                  key={activityId}
                  disableGutters
                  elevation={0}
                  expanded={isExpanded}
                  onChange={handleAccordionChange(activityId)}
                  sx={{
                    backgroundColor: rowBackgroundColor,
                    boxShadow: "none",
                    borderBottom: theme.palette.mode === "dark"
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid rgba(0,0,0,0.08)",
                    "&:before": {
                      display: "none",
                    },
                    "&.Mui-expanded": {
                      margin: 0,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        sx={{
                          color: theme.palette.mode === "dark"
                            ? alpha(theme.palette.common.white, 0.6)
                            : alpha(theme.palette.text.primary, 0.6)
                        }}
                      />
                    }
                    aria-controls={`panel-${index}-content`}
                    id={`panel-${index}-header`}
                    sx={{
                      minHeight: "56px",
                      padding: "0 16px 0 50px", // Fixed padding for all items
                      "& .MuiAccordionSummary-content": {
                        margin: "12px 0",
                        alignItems: "center",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        position: "relative",
                      }}
                    >
                      {/* Left icon space - always reserve the space */}
                      <Box
                        sx={{
                          position: "absolute",
                          left: "-34px",
                          width: "24px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {isPremiumRelated && (
                          <StarIcon
                            sx={{
                              color: "#d44402",
                              fontSize: "1.1rem",
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.95rem",
                          color: theme.palette.mode === "dark"
                            ? theme.palette.common.white
                            : theme.palette.text.primary,
                          fontWeight: 400,
                          transition: "color 0.3s ease",
                        }}
                      >
                        {userActivity.description || "Activity"}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  {isExpanded && (
                    <AccordionDetails
                      sx={{
                        padding: "12px 16px 16px 50px", // Match the padding of the summary
                        backgroundColor: rowBackgroundColor,
                        borderTop: theme.palette.mode === "dark"
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "1px solid rgba(0,0,0,0.08)",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Box
                        sx={{
                          color: theme.palette.mode === "dark"
                            ? alpha(theme.palette.common.white, 0.6)
                            : alpha(theme.palette.text.primary, 0.6),
                          fontSize: "0.85rem",
                          mb: 2,
                          transition: "color 0.3s ease",
                        }}
                      >
                        {userActivity.created_at
                          ? formatDateDurationForJoinedDateExtended(
                              userActivity.created_at
                            )
                          : "Unknown date"}
                      </Box>
                      {/* Show additional details for premium activities */}
                      {isPremiumRelated && metaData && (
                        <Box sx={{ mt: 1.5 }}>
                          {metaData.plan_type && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.mode === "dark"
                                  ? alpha(theme.palette.common.white, 0.7)
                                  : alpha(theme.palette.text.primary, 0.7),
                                fontSize: "0.85rem",
                                mb: 0.8,
                                transition: "color 0.3s ease",
                              }}
                            >
                              Plan:{" "}
                              <strong
                                style={{
                                  color: theme.palette.mode === "dark"
                                    ? alpha(theme.palette.common.white, 0.9)
                                    : theme.palette.text.primary
                                }}
                              >
                                {capitalizeEachFirstWord(metaData.plan_type)}
                              </strong>
                            </Typography>
                          )}
                          {metaData.payment_method && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.mode === "dark"
                                  ? alpha(theme.palette.common.white, 0.7)
                                  : alpha(theme.palette.text.primary, 0.7),
                                fontSize: "0.85rem",
                                mb: 0.8,
                                transition: "color 0.3s ease",
                              }}
                            >
                              Payment method:{" "}
                              <strong
                                style={{
                                  color: theme.palette.mode === "dark"
                                    ? alpha(theme.palette.common.white, 0.9)
                                    : theme.palette.text.primary
                                }}
                              >
                                {metaData.payment_method}
                              </strong>
                            </Typography>
                          )}
                          {metaData.start_date && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.mode === "dark"
                                  ? alpha(theme.palette.common.white, 0.7)
                                  : alpha(theme.palette.text.primary, 0.7),
                                fontSize: "0.85rem",
                                mb: 0.8,
                                transition: "color 0.3s ease",
                              }}
                            >
                              Start date:{" "}
                              <strong
                                style={{
                                  color: theme.palette.mode === "dark"
                                    ? alpha(theme.palette.common.white, 0.9)
                                    : theme.palette.text.primary
                                }}
                              >
                                {formatDateDurationForJoinedDateExtended(
                                  metaData.start_date,
                                  false
                                )}
                              </strong>
                            </Typography>
                          )}
                          {metaData.end_date && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.mode === "dark"
                                  ? alpha(theme.palette.common.white, 0.7)
                                  : alpha(theme.palette.text.primary, 0.7),
                                fontSize: "0.85rem",
                                mb: 0.8,
                                transition: "color 0.3s ease",
                              }}
                            >
                              End date:{" "}
                              <strong
                                style={{
                                  color: theme.palette.mode === "dark"
                                    ? alpha(theme.palette.common.white, 0.9)
                                    : theme.palette.text.primary
                                }}
                              >
                                {formatDateDurationForJoinedDateExtended(
                                  metaData.end_date,
                                  false
                                )}
                              </strong>
                            </Typography>
                          )}
                          {metaData.amount && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.mode === "dark"
                                  ? alpha(theme.palette.common.white, 0.7)
                                  : alpha(theme.palette.text.primary, 0.7),
                                fontSize: "0.85rem",
                                mb: 0.8,
                                transition: "color 0.3s ease",
                              }}
                            >
                              Amount:{" "}
                              <strong
                                style={{
                                  color: theme.palette.mode === "dark"
                                    ? alpha(theme.palette.common.white, 0.9)
                                    : theme.palette.text.primary
                                }}
                              >
                                ${metaData.amount} {metaData.currency || "USD"}
                              </strong>
                            </Typography>
                          )}
                          {metaData.coins_spent && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.mode === "dark"
                                  ? alpha(theme.palette.common.white, 0.7)
                                  : alpha(theme.palette.text.primary, 0.7),
                                fontSize: "0.85rem",
                                mb: 0.8,
                                transition: "color 0.3s ease",
                              }}
                            >
                              Coins spent:{" "}
                              <strong
                                style={{
                                  color: theme.palette.mode === "dark"
                                    ? alpha(theme.palette.common.white, 0.9)
                                    : theme.palette.text.primary
                                }}
                              >
                                {metaData.coins_spent}
                              </strong>
                            </Typography>
                          )}
                          {metaData.hasOwnProperty("auto_renew") && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.mode === "dark"
                                  ? alpha(theme.palette.common.white, 0.7)
                                  : alpha(theme.palette.text.primary, 0.7),
                                fontSize: "0.85rem",
                                mb: 0.8,
                                transition: "color 0.3s ease",
                              }}
                            >
                              Auto-renewal:{" "}
                              <strong
                                style={{
                                  color: theme.palette.mode === "dark"
                                    ? alpha(theme.palette.common.white, 0.9)
                                    : theme.palette.text.primary
                                }}
                              >
                                {metaData.auto_renew ? "Enabled" : "Disabled"}
                              </strong>
                            </Typography>
                          )}
                          {metaData.hasOwnProperty("canceled") && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.mode === "dark"
                                  ? alpha(theme.palette.common.white, 0.7)
                                  : alpha(theme.palette.text.primary, 0.7),
                                fontSize: "0.85rem",
                                mb: 0.8,
                                transition: "color 0.3s ease",
                              }}
                            >
                              Status:{" "}
                              <strong
                                style={{
                                  color: theme.palette.mode === "dark"
                                    ? alpha(theme.palette.common.white, 0.9)
                                    : theme.palette.text.primary
                                }}
                              >
                                {metaData.canceled ? "Canceled" : "Active"}
                              </strong>
                            </Typography>
                          )}
                          {metaData.reason && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.mode === "dark"
                                  ? alpha(theme.palette.common.white, 0.7)
                                  : alpha(theme.palette.text.primary, 0.7),
                                fontSize: "0.85rem",
                                mb: 0.8,
                                transition: "color 0.3s ease",
                              }}
                            >
                              Reason:{" "}
                              <strong
                                style={{
                                  color: theme.palette.mode === "dark"
                                    ? alpha(theme.palette.common.white, 0.9)
                                    : theme.palette.text.primary
                                }}
                              >
                                {metaData.reason.replace(/_/g, " ")}
                              </strong>
                            </Typography>
                          )}
                        </Box>
                      )}
                      {/* Action buttons if needed */}
                      {metaData &&
                        metaData.can_visit_profile &&
                        metaData.other_username && (
                          <Box
                            sx={{
                              mt: 2,
                              display: "flex",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Chip
                              label={"View Profile"}
                              color={"info"}
                              size="small"
                              onClick={(e) =>
                                loadANewPage(`/${metaData.other_username}`)
                              }
                              sx={{
                                cursor: "pointer",
                                fontSize: "0.75rem",
                                height: "28px",
                              }}
                            />
                          </Box>
                        )}
                    </AccordionDetails>
                  )}
                </Accordion>
              );
            })}
          </Box>

          {/* Load More button replacing infinite scroll */}
          {hasMoreData && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                p: 2,
                backgroundColor: theme.palette.mode === "dark" ? "#1a1a1f" : "#ffffff",
                borderTop: theme.palette.mode === "dark"
                  ? "1px solid rgba(255,255,255,0.1)"
                  : "1px solid rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleLoadMore}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontSize: "0.85rem",
                  minWidth: "120px",
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={16}
                      sx={{ mr: 1, color: theme.palette.primary.main }}
                    />
                    Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </Button>
            </Box>
          )}

          {/* End of data message */}
          {!loading &&
            !hasMoreData &&
            currentUserActivities.response.length > 0 && (
              <Box
                sx={{
                  textAlign: "center",
                  padding: "12px",
                  color: theme.palette.mode === "dark"
                    ? alpha(theme.palette.common.white, 0.6)
                    : alpha(theme.palette.text.primary, 0.6),
                  fontSize: "13px",
                  borderTop: theme.palette.mode === "dark"
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "1px solid rgba(0,0,0,0.08)",
                  backgroundColor: theme.palette.mode === "dark" ? "#1a1a1f" : "#ffffff",
                  transition: "all 0.3s ease",
                }}
              >
                <Typography variant="caption">
                  No more activities to load
                </Typography>
              </Box>
            )}
        </>
      ) : (
        // Empty state
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
            color: theme.palette.mode === "dark"
              ? alpha(theme.palette.common.white, 0.6)
              : alpha(theme.palette.text.primary, 0.6),
            textAlign: "center",
            backgroundColor: theme.palette.mode === "dark" ? "#1a1a1f" : "#ffffff",
            transition: "all 0.3s ease",
          }}
        >
          <FolderOpenIcon
            sx={{
              fontSize: 40,
              color: theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.3)
                : alpha(theme.palette.text.primary, 0.3),
              mb: 2,
            }}
          />
          <Typography
            variant="body1"
            sx={{
              mb: 1,
              color: theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.8)
                : theme.palette.text.primary,
              transition: "color 0.3s ease",
            }}
          >
            No activities found
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
              color: theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.6)
                : alpha(theme.palette.text.primary, 0.6),
              transition: "color 0.3s ease",
            }}
          >
            Your recent account activities will appear here
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
